import { fetchCustomerData } from "@/redux/slices/customerSlice";
import { fetchVoucherByCode, setQrCode, clearVoucher } from "@/redux/slices/qrSlice";
import { useNavigate } from "react-router-dom";
import { Loader2, QrCode } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useRef, useState } from "react";
import { assignVoucherdata } from "@/services/customer";
import { updateQr } from "@/services/auth";

import { getT } from '../i18n/translations';

const SLOGAN_MAX_LENGTH = 100;

export default function Home() {
  const t = getT();
  const name = localStorage.getItem("name");
  const mobile = localStorage.getItem("mobile") || localStorage.getItem("phone") || localStorage.getItem("phoneNumber") || "";
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showCodePopup, setShowCodePopup] = useState(false);
  const [manualCode, setManualCode] = useState("");
  const [status, setStatus] = useState("");
  const [message, setMessage] = useState("");
  const [voucherValue, setVoucherValue] = useState(null);
  const [showPopup, setShowPopup] = useState(false);

  const [assignedVoucherRecord, setAssignedVoucherRecord] = useState(null);
  const [showSloganPopup, setShowSloganPopup] = useState(false);
  const [sloganText, setSloganText] = useState("");
  const [sloganError, setSloganError] = useState("");
  const [sloganSubmitting, setSloganSubmitting] = useState(false);

  const { data: customer, loading } = useSelector((state) => state.customer);
  const { code: qrCode, voucher, loading: voucherLoading } = useSelector((state) => state.qr);
  const token = useSelector((state) => state.auth.token);
  const lastProcessedCode = useRef(null);

  const getInitials = (name) => {
    if (!name) return "";
    const words = name.trim().split(" ");
    return words.length === 1 ? words[0][0] : words[0][0] + words[1][0];
  };

  useEffect(() => {
    const customerCode = localStorage.getItem("customercode");
    dispatch(fetchCustomerData(customerCode));
  }, []);

  useEffect(() => {
    if (!qrCode || !token) return;
    lastProcessedCode.current = null;
    dispatch(clearVoucher());
    dispatch(fetchVoucherByCode({ token, code: qrCode }));
  }, [qrCode, token]);

  useEffect(() => {
    if (!voucher || !qrCode || !customer) return;
    if (lastProcessedCode.current === qrCode) return;
    lastProcessedCode.current = qrCode;

    const data = voucher?.data?.voucher;

    if (!data) { setStatus("error"); setMessage(t.home_errInvalid); setShowPopup(true); return; }
    if (data?.voucherUsed === true) { setStatus("error"); setMessage(t.home_errUsed); setShowPopup(true); return; }
    const currentWallet = customer?.WalletSummary ?? [];
    const totalVouchers = currentWallet.filter((item) => item.type === "voucher").length;
    if (totalVouchers >= 10) {
      setStatus("error");
      setMessage(t.home_errMaxLimit);
      setShowPopup(true);
      return;
    }

    if (data?.isAssigned === true) {
      const alreadyMine = currentWallet.some(
        (item) => item.voucherCode === data?.code || item.voucherCode === qrCode
      );
      if (alreadyMine) {
        setStatus("already_mine");
        setMessage(t.home_errAssignedMine);
        setVoucherValue(data?.vouchervalue ?? 0);
        setShowPopup(true);
        return;
      } else {
        setStatus("error");
        setMessage(t.home_errAssignedOther);
        setShowPopup(true);
        return;
      }
    }

    if (new Date(data?.expiryDate) < new Date()) { setStatus("error"); setMessage(t.home_errExpired); setShowPopup(true); return; }

    const value = data?.vouchervalue ?? 0;
    setVoucherValue(value);


      assignVoucherdata(token, {
        client: "MER000479",
        voucherTemplate: data?.voucherTemplateCode,
        voucher: data?.code,
        Value: value,
        CustomerCode: localStorage.getItem("customercode"),
      }).then(() => {
        const customerCode = localStorage.getItem("customercode");
        dispatch(fetchCustomerData(customerCode));
      });


    setAssignedVoucherRecord(data);
    setSloganText("");
    setSloganError("");
    setShowSloganPopup(true);
  }, [voucher, qrCode, customer]);

  const handleSloganSubmit = async () => {
    const trimmedSlogan = sloganText.trim();
    if (!trimmedSlogan) { setSloganError(t.slogan_errEmpty); return; }

    const currentWallet = customer?.WalletSummary ?? [];
    const voucherEntry = currentWallet.find(
      (item) => item.voucherCode === assignedVoucherRecord?.code || item.voucherCode === qrCode
    );
    if (!voucherEntry) { setSloganError(t.slogan_errFailed); return; }

    const id = voucherEntry?.id ?? voucherEntry?._id;
    setSloganError("");
    setSloganSubmitting(true);
    try {
      const res = await updateQr(token, id, {}, trimmedSlogan);
      if (res && res.ok) {
        setShowSloganPopup(false);
        setStatus("success");
        setMessage(t.home_errSuccess);
        setShowPopup(true);
      } else {
        setSloganError(t.slogan_errFailed);
      }
    } catch {
      setSloganError(t.slogan_errFailed);
    } finally {
      setSloganSubmitting(false);
    }
  };

  const handleClosePopup = () => {
    setShowPopup(false);
    dispatch(setQrCode(null));
    localStorage.removeItem("qrCode");
  };

  const handleScanAnother = () => {
    setShowPopup(false);
    dispatch(setQrCode(null));
    localStorage.removeItem("qrCode");
    navigate("/scan");
  };

  const handleManualSubmit = () => {
    const code = manualCode.trim().toUpperCase();
    if (!code) return;
    dispatch(setQrCode(null));
    requestAnimationFrame(() => { dispatch(setQrCode(code)); });
    setShowCodePopup(false);
    setManualCode("");
  };

  const walletSummary = customer?.WalletSummary ?? [];

  const voucherBalance =
    walletSummary
      .filter((item) => item.type === "voucher" && item.VoucherUsed === false)
      .reduce((sum, item) => sum + (item.earnedValue || 0), 0) || 0;

  const totalEarned =
    walletSummary
      .filter((item) => item.type === "voucher")
      .reduce((sum, item) => sum + (item.earnedValue || 0), 0) || 0;

  const totalTransferred =
    walletSummary
      .filter((item) => item.type === "voucher" && item.VoucherUsed === true)
      .reduce((sum, item) => sum + (item.earnedValue || 0), 0) || 0;

  return (
    <div
     className="h-screen flex justify-center overflow-hidden"
      style={{
        fontFamily: "'Poppins', sans-serif",
        backgroundColor: '#f0f4f0',
        backgroundImage: 'radial-gradient(circle, #c8d8c8 1px, transparent 1px)',
        backgroundSize: '22px 22px',
      }}
    >
<div className="w-full max-w-md h-full flex flex-col overflow-y-auto overflow-x-hidden">

        {(loading || voucherLoading) && (
          <div className="fixed inset-0 bg-black/20 flex justify-center items-center z-50 backdrop-blur-sm">
            <Loader2 className="animate-spin h-12 w-12 text-[#2d5a2d]" />
          </div>
        )}

   <div className="px-4 pt-5 pb-6 space-y-4 flex-1 overflow-x-hidden">
          <div
            className="rounded-3xl p-6 relative overflow-hidden min-h-[260px]"
            style={{ background: 'linear-gradient(160deg, #2d5a2d 0%, #1a3a1a 60%, #3a6b1a 100%)' }}
          >
            <div className="flex justify-between items-start mb-4 relative z-10">
              <div>
                <p className="text-[11px] font-semibold tracking-widest text-[#a8d08a] uppercase">{t.home_welcome}</p>
                <p className="text-[22px] font-bold text-white leading-tight mt-1">{customer?.customer?.firstName}</p>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>

                <div
                  onClick={() => navigate("/profile")}
                  className="w-11 h-11 rounded-full bg-white/20 flex items-center justify-center cursor-pointer active:scale-95"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                    <path d="M20 21a8 8 0 0 0-16 0" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                </div>
              </div>
            </div>

            <div
              className="rounded-2xl p-5 mt-4"
              style={{ backgroundColor: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.2)' }}
            >
              <div className="grid grid-cols-3 gap-4 mb-5">
                {[
                  { label: t.home_totalEarned, value: `₹${totalEarned}` },
                  { label: t.home_transferred, value: `₹${totalTransferred}` },
                  { label: t.home_balance, value: `₹${voucherBalance}` },
                ].map(({ label, value }) => (
                  <div key={label} className="text-center">
                    <p className="text-[9px] font-semibold tracking-wider text-[#a8d08a] uppercase mb-1">{label}</p>
                    <p className="text-[17px] font-bold text-white">{value}</p>
                  </div>
                ))}
              </div>
              <button
                onClick={() => navigate("/redeem-cash")}
                className="w-full py-3 rounded-2xl text-sm font-semibold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
                style={{ backgroundColor: 'rgba(255,255,255,0.12)', color: '#fff', border: '1px solid rgba(255,255,255,0.2)' }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="2" y="5" width="20" height="14" rx="2" />
                  <path d="M16 12h.01" />
                </svg>
                {t.home_viewEarnings}
              </button>
            </div>

            <button
              onClick={() => navigate("/slogan")}
              className="w-full py-4 mt-5 rounded-2xl text-[15px] font-bold flex items-center justify-center gap-3 active:scale-[0.98] transition-transform"
              style={{ backgroundColor: '#c8d400', color: '#1a2e00' }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="black">
                <path d="M6 2h12v3h3v3c0 3.31-2.69 6-6 6h-1v2h3v2H7v-2h3v-2H9c-3.31 0-6-2.69-6-6V5h3V2zm0 5H4v1c0 2.21 1.79 4 4 4V7zm12 0v5c2.21 0 4-1.79 4-4V7h-2z" />
              </svg>
              {t.home_grandPrizeBtn}
            </button>
          </div>

          {/* ── PROMO CODE CARD ── */}
          <div className="bg-white rounded-2xl px-5 py-5 shadow-sm">
            <p className="text-[16px] font-bold text-[#1a2e1a] mb-1">{t.home_promoTitle}</p>
            <p className="text-[12px] text-gray-400 mb-4">{t.home_promoSubtitle}</p>
            <button
              onClick={() => navigate("/scan")}
              className="w-full py-3.5 rounded-xl text-[14px] font-semibold text-white flex items-center justify-center gap-2.5 active:scale-[0.98] transition-transform"
              style={{ backgroundColor: '#2d5a2d' }}
            >
              <QrCode size={18} />
              {t.home_scanBtn}
            </button>
          </div>

     

        </div>
 <div className="w-full py-3 border-t border-gray-200 mt-auto">
        <p className="text-[13px] text-black uppercase text-center tracking-wide">
          © {new Date().getFullYear()} Gencrest
        </p>
      </div>
        {/* ── ENTER CODE POPUP ── */}
        {showCodePopup && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
            <div className="bg-white w-[88%] max-w-sm rounded-2xl p-6 relative mx-4">
              <button
                onClick={() => { setShowCodePopup(false); setManualCode(""); }}
                className="absolute top-3 right-4 text-gray-400 text-xl"
              >✖</button>
              <h2 className="text-[16px] font-bold mb-4 text-center text-[#1a2e1a]">{t.home_enterCodeTitle}</h2>
              <input
                value={manualCode}
                onChange={(e) => setManualCode(e.target.value.toUpperCase())}
                onKeyDown={(e) => e.key === "Enter" && handleManualSubmit()}
                placeholder={t.home_enterCodePlaceholder}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm mb-4 outline-none focus:border-[#2d5a2d]"
                autoFocus
              />
              <button
                onClick={handleManualSubmit}
                disabled={!manualCode.trim()}
                className="w-full text-white py-3 rounded-xl font-semibold disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.98] transition-transform"
                style={{ backgroundColor: "#2d5a2d" }}
              >
                {t.home_submit}
              </button>
            </div>
          </div>
        )}

        {/* ── SLOGAN ENTRY POPUP (mandatory after voucher assignment) ── */}
        {showSloganPopup && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 px-4">
            <div className="bg-white rounded-3xl shadow-2xl p-6 w-full max-w-sm mx-4">
              <div className="text-center mb-4">
                <div className="w-14 h-14 rounded-full bg-[#a8d08a]/20 border-2 border-[#a8d08a]/40 flex items-center justify-center mx-auto mb-3">
                  <span className="text-2xl">✏️</span>
                </div>
                <p className="text-[11px] font-semibold tracking-widest text-[#4a7c59] uppercase mb-1">
                  {t.slogan_contestLabel}
                </p>
                <h2 className="text-[18px] font-bold text-[#1a2e1a]">{t.slogan_enterTitle}</h2>
                <p className="text-[12px] text-gray-400 mt-1 leading-relaxed">{t.slogan_enterSubtitle}</p>
              </div>
              <textarea
                rows={4}
                value={sloganText}
                maxLength={SLOGAN_MAX_LENGTH}
                onChange={(e) => { setSloganText(e.target.value); setSloganError(""); }}
                placeholder={t.slogan_placeholder}
                className="w-full border-[1.5px] border-[#e0e8e0] rounded-xl px-4 py-3 text-sm text-[#1a2e1a] resize-none outline-none focus:border-[#3a6b3a] transition-colors placeholder:text-[#b0c4b0]"
                autoFocus
              />
              <div className="flex justify-between items-center mt-2 mb-4">
                {sloganError ? (
                  <p className="text-xs text-red-500 flex items-center gap-1">
                    <span>⚠</span> {sloganError}
                  </p>
                ) : <div />}
                <div
                  className={`text-[11px] font-medium px-2 py-1 rounded-full ${sloganText.length > 90
                      ? "bg-red-100 text-red-600"
                      : sloganText.length > 70
                        ? "bg-yellow-100 text-yellow-600"
                        : "bg-gray-100 text-gray-500"
                    }`}
                >
                  {sloganText.length}/{SLOGAN_MAX_LENGTH}
                </div>
              </div>
              <button
                onClick={handleSloganSubmit}
                disabled={sloganSubmitting || !sloganText.trim()}
                className={`w-full py-3.5 rounded-2xl text-[15px] font-bold text-white flex items-center justify-center gap-2 active:scale-[0.98] transition-transform ${sloganSubmitting || !sloganText.trim() ? "opacity-60 cursor-not-allowed" : ""}`}
                style={{ backgroundColor: "#2d5a2d" }}
              >
                {sloganSubmitting ? (
                  <><Loader2 className="animate-spin h-4 w-4" /> {t.slogan_submitting}</>
                ) : (
                  t.slogan_submit
                )}
              </button>
            </div>
          </div>
        )}

        {/* ── VOUCHER STATUS POPUP ── */}
        {showPopup && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
            <div className="bg-white w-[88%] max-w-sm rounded-2xl p-6 relative text-center mx-4">
              <button onClick={handleClosePopup} className="absolute top-3 right-4 text-gray-400 text-xl">✖</button>

              {status === "already_mine" ? (
                <div className="pt-2">
                  <div
                    className="w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4 text-2xl"
                    style={{ backgroundColor: '#eaf3de', border: '2px solid #a8d08a' }}
                  >
                    ✓
                  </div>
                  <h2 className="text-[20px] font-bold mb-2 text-[#1a2e1a]">{t.home_alreadyAssignedTitle}</h2>
                  <button
                    onClick={() => { handleClosePopup(); navigate("/scan"); }}
                    className="w-full text-white py-3.5 rounded-xl font-semibold mb-3 active:scale-[0.98] transition-transform"
                    style={{ backgroundColor: "#2d5a2d" }}
                  >
                    {t.home_tryAnother}
                  </button>
                </div>
              ) : status === "success" ? (
                <>
                  {voucherValue > 0 ? (
                    <div className="pt-2">
                      <h2 className="text-[22px] font-bold text-[#1a2e1a] mb-2">{t.home_congrats}</h2>
                      <p className="text-[14px] text-gray-500 leading-relaxed mb-1">{t.home_youEarned}</p>
                      <p className="text-[14px] text-gray-500 leading-relaxed mb-3">{t.home_cashbackRewards}</p>
                      <p className="text-[44px] font-bold mb-6" style={{ color: '#2d5a2d' }}>₹{voucherValue}</p>
                      <button
                        onClick={() => { handleClosePopup(); navigate("/redeem-cash"); }}
                        className="w-full text-white py-3.5 rounded-xl font-semibold mb-3 flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
                        style={{ backgroundColor: "#2d5a2d" }}
                      >
                        {t.home_viewEarningsBtn}
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M5 12h14M12 5l7 7-7 7" />
                        </svg>
                      </button>
                      <button
                        onClick={handleScanAnother}
                        className="w-full py-3.5 rounded-xl font-semibold mb-3 text-[#1a2e1a] border-[1.5px] border-[#e0e8e0] active:scale-[0.98] transition-transform text-[14px]"
                      >
                        {t.home_scanAnother}
                      </button>
                      <button
                        onClick={() => { handleClosePopup(); navigate("/slogan"); }}
                        className="w-full py-3.5 rounded-xl font-bold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform text-[14px]"
                        style={{ backgroundColor: '#c8d400', color: '#1a2e00' }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="#1a2e00">
                          <path d="M6 2h12v3h3v3c0 3.31-2.69 6-6 6h-1v2h3v2H7v-2h3v-2H9c-3.31 0-6-2.69-6-6V5h3V2zm0 5H4v1c0 2.21 1.79 4 4 4V7zm12 0v5c2.21 0 4-1.79 4-4V7h-2z" />
                        </svg>
                        {t.home_grandPrizeBtn}
                      </button>
                    </div>
                  ) : (
                    <div className="pt-2">
                      <div className="w-16 h-16 mx-auto rounded-full bg-green-100 flex items-center justify-center mb-4 text-3xl">🎉</div>
                      {customer?.customer?.slogan
                        ? <h2 className="text-[16px] font-bold mb-2 text-[#1a2e1a]">{t.home_inContestTitle}</h2>
                        : <h2 className="text-[16px] font-bold mb-2 text-[#1a2e1a]">{t.home_congratsTitle}</h2>
                      }
                      <div className="bg-gray-50 border rounded-xl p-4 mt-2 text-left">
                        {customer?.customer?.slogan ? (
                          <>
                            <p className="text-sm text-gray-500 leading-relaxed">{t.home_contestReceivedMsg}</p>
                            <button
                              onClick={() => { handleClosePopup(); navigate("/scan"); }}
                              className="w-full text-white py-3 mt-2 rounded-xl text-sm font-semibold active:scale-[0.98] transition-transform"
                              style={{ backgroundColor: "#2d5a2d" }}
                            >
                              {t.home_scanMoreBtn}
                            </button>
                            <button
                              onClick={() => { handleClosePopup(); navigate("/home"); }}
                              className="w-full text-white py-3 mt-3 rounded-xl text-sm font-semibold active:scale-[0.98] transition-transform"
                              style={{ backgroundColor: "#2d5a2d" }}
                            >
                              {t.home_goHome}
                            </button>
                          </>
                        ) : (
                          <>
                            <p className="text-sm text-gray-500 mb-4 leading-relaxed">{t.home_eligibleMsg}</p>
                            <button
                              onClick={() => { handleClosePopup(); navigate("/slogan"); }}
                              className="w-full text-white py-3 rounded-xl text-sm font-semibold active:scale-[0.98] transition-transform"
                              style={{ backgroundColor: "#2d5a2d" }}
                            >
                              {t.home_participate}
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </>
              ) : status === "assigned_another_user" ? (
                <div className="pt-2">
                  <div
                    className="w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4 text-2xl"
                    style={{ backgroundColor: '#eaf3de', border: '2px solid #a8d08a' }}
                  >
                    ✓
                  </div>
                  <h2 className="text-[20px] font-bold mb-2 text-[#1a2e1a]">{t.home_alreadyAssignedTitle}</h2>
                  <button
                    onClick={() => { handleClosePopup(); navigate("/scan"); }}
                    className="w-full text-white py-3.5 rounded-xl font-semibold mb-3 active:scale-[0.98] transition-transform"
                    style={{ backgroundColor: "#2d5a2d" }}
                  >
                    {t.home_tryAnother}
                  </button>
                </div>
              ) : (
                <div className="pt-2">
                  <div className="w-16 h-16 mx-auto rounded-full bg-red-100 flex items-center justify-center mb-4 text-2xl text-red-500">✖</div>
                  <h2 className="text-[16px] font-bold mb-2 text-[#1a2e1a]">{t.home_verificationFailed}</h2>
                  <p className="text-sm text-gray-500 mb-5">{message}</p>
                  <button
                    onClick={handleClosePopup}
                    className="w-full text-white py-3 rounded-xl font-semibold active:scale-[0.98] transition-transform"
                    style={{ backgroundColor: "#2d5a2d" }}
                  >
                    {t.home_tryAgain}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}